import { Fragment, useMemo, useState } from "react";
import { ChevronIcon } from "vega-icons";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { StyledPlaceholder } from "@vega-core/components/Loaders/StyledPlaceholder";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@vega-core/components/shadcn/popover";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { RemoveAccessButton } from "./RemoveAccessButton";

type Props = {
  value?: string;
  disabled?: boolean;
  options?: RoleNew[];
  onChange?: (selectedItem: RoleNew) => void;
  onRemoveAccess?: VoidFunction;
  showRemoveButton?: boolean;
  isLoading?: boolean;
};

export const RoleSelector = ({
  options = [],
  onChange,
  disabled,
  value,
  onRemoveAccess,
  showRemoveButton = true,
  isLoading,
}: Props) => {
  const [open, setOpen] = useState(false);

  const selectedRole = useMemo(() => {
    if (value) {
      return options.find((option) => option.id === value);
    }
  }, [options, value]);

  if (isLoading) {
    return <StyledPlaceholder className="w-32 h-8" />;
  }

  if (isTruthy(disabled)) {
    return (
      <div className={cnTwMerge({ "text-text/disabled": !selectedRole })}>
        {selectedRole?.name ?? m.userManagement_permissions_noAccess()}
      </div>
    );
  }

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <div>
          <ThemedButton
            className={cnTwMerge(
              "border-none bg-transparent hover:bg-transparent hover:shadow-none! active:bg-transparent pr-0",
              { "text-text/disabled": !selectedRole },
            )}
            dataTestId={`${value}-button`}
            icon={
              <ChevronIcon className="h-4 w-4 shrink-0 text-text/secondary" />
            }
            iconPosition="right"
            variant="input"
          >
            {selectedRole?.name ?? m.userManagement_permissions_noAccess()}
          </ThemedButton>
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-auto p-0 bg-background/primary rounded-lg shadow border border-border/primary flex flex-col gap-0"
        data-testid="role-selector-popover"
      >
        <div className="py-2">
          {options.map((option) => (
            <Fragment key={option.id}>
              <div className="w-full px-2">
                <div
                  className="w-full cursor-pointer h-11 px-2 rounded-lg justify-start text-left hover:bg-background/secondary text-text/primary"
                  data-testid={`${option.id}-button`}
                  onClick={() => {
                    onChange && onChange(option);
                    setOpen(false);
                  }}
                >
                  <div className="flex flex-col">
                    <span className="body-base-medium">{option.name}</span>
                    {option.description ? (
                      <span className="body-small-regular text-text/secondary">
                        {option.description}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            </Fragment>
          ))}
        </div>
        <div className="w-full h-[1px] bg-border/secondary" />
        {isTruthy(selectedRole) &&
        showRemoveButton &&
        isTruthy(onRemoveAccess) ? (
          <div className="w-full p-2">
            <RemoveAccessButton onClick={onRemoveAccess} />
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};

import { type RoleNew } from "@vega-api/generated/types/roleNew";
